import { useCallback, useEffect, useRef, useState } from "react";

interface AudioWaveformProps {
  volume: number; // 0.0 à 1.0
  isActive: boolean;
  className?: string;
  compact?: boolean; // Mode compact pour l'indicateur intégré
}

// Constantes de configuration
const BAR_COUNT = 25; // Number of bars/dots to display
const SILENCE_THRESHOLD = Number(
  process.env.BUN_PUBLIC_SILENCE_THRESHOLD || 0.05,
); // Volume below this = silence (from env)
const BAR_MAX_HEIGHT = 28; // Maximum bar height in pixels (normal mode)
const BAR_MAX_HEIGHT_COMPACT = 12; // Maximum bar height in pixels (compact mode)
const BAR_MIN_HEIGHT = 4; // Minimum bar height in pixels
const BAR_MIN_HEIGHT_COMPACT = 2; // Minimum bar height in pixels (compact mode)
const UPDATE_INTERVAL_MS = Number(
  process.env.BUN_PUBLIC_AUDIO_UPDATE_INTERVAL_MS || 50,
); // How often to update the visualization

export function AudioWaveform({
  volume,
  isActive,
  className = "",
  compact = false,
}: AudioWaveformProps) {
  // Historique du volume, source de la visualisation en barres
  const [volumeHistory, setVolumeHistory] = useState<number[]>(() =>
    Array(BAR_COUNT).fill(0),
  );

  // Suit l'état de silence (léger délai, pour adoucir les transitions)
  const [isSilent, setIsSilent] = useState(true);
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastVolumeRef = useRef<number>(0);

  // Suivi lissé du volume
  const smoothedVolumeRef = useRef<number>(0);

  // Met à jour périodiquement l'historique du volume pendant l'enregistrement
  const updateVisualization = useCallback(() => {
    if (!isActive) {
      setVolumeHistory(Array(BAR_COUNT).fill(0));
      setIsSilent(true);
      return;
    }

    // Lissage du volume
    smoothedVolumeRef.current += (volume - smoothedVolumeRef.current) * 0.4;
    const smoothVolume = smoothedVolumeRef.current;
    lastVolumeRef.current = smoothVolume;

    // Sommes-nous en silence ?
    const currentlySilent = smoothVolume < SILENCE_THRESHOLD;

    if (currentlySilent) {
      // Passage en silence différé, pour une transition moins brutale
      if (!silenceTimeoutRef.current) {
        silenceTimeoutRef.current = setTimeout(() => {
          setIsSilent(true);
        }, 150);
      }
    } else {
      // Annule le minuteur de silence en attente et réaffiche les barres aussitôt
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      setIsSilent(false);
    }

    // Historique du volume : décalage à gauche, puis ajout de la nouvelle valeur
    setVolumeHistory((prev) => {
      const newHistory = [...prev.slice(1), smoothVolume];
      return newHistory;
    });
  }, [isActive, volume]);

  // Met en place l'intervalle de rafraîchissement
  useEffect(() => {
    // Réinitialise le lissage au démarrage de l'enregistrement
    if (isActive) {
      smoothedVolumeRef.current = 0;
    }

    const interval = setInterval(updateVisualization, UPDATE_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
    };
  }, [isActive, updateVisualization]);

  // Réinitialise quand le composant n'est plus actif
  useEffect(() => {
    if (!isActive) {
      setVolumeHistory(Array(BAR_COUNT).fill(0));
      setIsSilent(true);
      smoothedVolumeRef.current = 0;
    }
  }, [isActive]);

  // Hauteur de barre calculée depuis le volume
  const getBarHeight = (vol: number): number => {
    const minHeight = compact ? BAR_MIN_HEIGHT_COMPACT : BAR_MIN_HEIGHT;
    const maxHeight = compact ? BAR_MAX_HEIGHT_COMPACT : BAR_MAX_HEIGHT;

    if (vol < SILENCE_THRESHOLD) {
      return minHeight;
    }
    // Projette le volume sur la plage de hauteurs
    const normalizedVolume =
      (vol - SILENCE_THRESHOLD) / (1 - SILENCE_THRESHOLD);
    return minHeight + normalizedVolume * (maxHeight - minHeight);
  };

  // Hauteur du conteneur, selon le mode
  const containerHeight = compact ? "h-4" : "h-8";
  // Couleur des barres : blanc en mode compact (sur fond rouge), rouge sinon
  const barColor = compact ? "bg-white" : "bg-red-500";
  const dotColor = compact ? "bg-white/60" : "bg-gray-400";
  const inactiveColor = compact
    ? "bg-white/30"
    : "bg-gray-300";

  // Hors enregistrement : ligne plate
  if (!isActive) {
    return (
      <div
        className={`flex items-center justify-center ${containerHeight} gap-0.5 ${className}`}
      >
        {Array.from({ length: BAR_COUNT }).map((_, i) => (
          <div
            key={i}
            className={`w-1 rounded-full ${inactiveColor} self-center`}
            style={{ height: "2px" }}
          />
        ))}
      </div>
    );
  }

  // Enregistrement en cours : points ou barres
  return (
    <div
      className={`flex items-center justify-center ${containerHeight} gap-0.5 overflow-hidden ${className}`}
    >
      {volumeHistory.map((vol, i) => {
        const height = getBarHeight(vol);

        // En silence : petits points animés en pulsation
        if (isSilent) {
          return (
            <div
              key={i}
              className={`w-1 h-1 rounded-full ${dotColor} animate-pulse self-center`}
              style={{ animationDelay: `${i * 30}ms` }}
            />
          );
        }

        // En activité : barres dont la hauteur suit le volume
        return (
          <div
            key={i}
            className={`w-1 rounded-full ${barColor} transition-all duration-75 ease-out self-center`}
            style={{ height: `${height}px` }}
          />
        );
      })}
    </div>
  );
}
